import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from '@emotion/styled';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';

// START OF VALIDATION FORM STYLES
const LoginForm = styled.form`
	padding: 15px;
	margin-bottom: 0px;
	.form-group {
		display: flex;
		flex-direction: column;
		margin-bottom: 10px;
	}
	label {
		font-size: 16px;
		font-weight: 500;
		margin-bottom: 5px;
	}
	label span {
		color: #a94442;
	}
	input {
		width: 100%;
		height: 34px;
		padding-left: 10px;
		margin-bottom: 0px;
		&:focus {
			outline: none;
		}
	}
	label.error {
		color: #a94442;
	}
	input.error {
		border: 1px solid #a94442;
	}
	.input-response {
		font-size: 13px;
		color: #a94442;
		padding-top: 5px;
	}
	.modal-footer {
		margin: 0px -15px;
		padding: 20px 15px 0px;
		.button-wrap {
			display: flex;
			justify-content: flex-end;
			padding: 0px;
			.btn {
				border: none;
				border-radius: 0px;
				font-weight: 500;
				font-size: 14px;
			}
			.guest {
				background-color: transparent;
				border: 1px solid #000;
				margin-right: 8px;
				&:hover {
					background-color: #e6e5e5;
				}
			}
		}
		.progress {
			width: 100%;
			margin: 0px;
		}
	}
`;

const ShowError = styled.div`
	padding: 10px 15px;
	background-color: #f2dede;
	border: 1px solid #ebccd1;
	border-radius: 4px;
	color: #a94442;
	margin-bottom: 15px;
	p {
		font-size: 16px;
		font-weight: 400;
		line-height: 22px;
	}
`;

const ForgotPassword = styled.div`
	h4 {
		font-size: 18px;
		font-weight: bold;
		padding: 10px 0px;
		line-height: 20px;
	}
	p {
		font-size: 16px;
		a {
			color: #0274a9;
			&:hover {
				color: #325f88;
			}
		}
	}
`;

// END OF VALIDATION FORM STYLES

// START OF FUNCTIONAL COMPONENT
const Validate = ({ handleClose }) => {
	/*
		Setting the state for login error to false
	*/
	const [errorState, setErrorState] = useState({
		exists: false,
		message: null
	});
	const [showProgressBar, setShowProgressBar] = useState(false);
	// eslint-disable-next-line no-undef
	const initialEmailValue = pageLayer[0].email.length ? pageLayer[0].email : '';

	return (
		<Formik
			initialValues={{
				email: initialEmailValue,
				password: ''
			}}
			onSubmit={(values, { setSubmitting }) => {
				setShowProgressBar(true);
				setTimeout(() => {
					const submitObject = {
						email: values.email,
						password: values.password
					};

					const userLogin = JSON.stringify(submitObject);
					fetch('/api/authentication/login', {
						method: 'POST',
						body: userLogin,
						headers: {
							'Content-Type': 'application/json'
						}
					})
						.then((response) => {
							if (response.ok) {
								return response.json();
							} else {
								return response.text().then((error) => {
									throw new Error(error);
								});
							}
						})
						.then(() => {
							setShowProgressBar(false);
							location.reload();
							setErrorState({ exists: false, message: null });
							setSubmitting(false);
						})
						.catch((error) => {
							if (error.message) {
								let message = JSON.parse(error.message);
								setErrorState({
									exists: true,
									message: message.statusMessage || false
								});
							} else {
								setErrorState({
									exists: true,
									message: `We're sorry, we can't log you in at this time, please try again later`
								});
							}
							setShowProgressBar(false);
							setSubmitting(false);
						});
				}, 500);
			}}
			/*
				This will check the user input for email and password
				then validate to make sure there are no errors.
			*/
			validationSchema={Yup.object().shape({
				email: Yup.string()
					.required('This field is required')
					.matches(
						/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
						'Please enter a valid email address.'
					),
				password: Yup.string()
					.required('This field is required')
					.min(
						8,
						'Password are a minimum of 8 characters and contain at least 1 number and 1 letter.'
					)
					.matches(
						/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/,
						'Passwords are 8-16 letters and at least one number.'
					)
			})}
		>
			{(props) => {
				const {
					values,
					touched,
					errors,
					isSubmitting,
					handleChange,
					handleBlur,
					handleSubmit
				} = props;

				/*
					The content within the form
				*/
				return (
					<div>
						<Modal.Header closeButton>
							<h4 className='modal-title primary-font highlight'>
								Log into your account for faster checkout
							</h4>
						</Modal.Header>
						<LoginForm onSubmit={handleSubmit}>
							<ShowError className={errorState.exists ? '' : 'hide'}>
								<p>
									{errorState.message ||
										`The email address or password you entered is incorrect. Please
							double-check your entry and try again (and remember, passwords are
							case-sensitive)`}
								</p>
							</ShowError>
							<div className='form-group'>
								<label
									htmlFor='email'
									className={errors.email && touched.email && 'error'}
								>
									<span>*</span> Email Address
								</label>
								<input
									name='email'
									type='text'
									placeholder='Email Address'
									value={values.email}
									onChange={handleChange}
									onBlur={handleBlur}
									className={errors.email && touched.email && 'error'}
								/>
								{errors.email && touched.email && (
									<div className='input-response'>{errors.email}</div>
								)}
							</div>
							<div className='form-group'>
								<label
									htmlFor='password'
									className={errors.password && touched.password && 'error'}
								>
									<span>*</span> Password
								</label>
								<input
									name='password'
									type='password'
									placeholder='Password'
									value={values.password}
									onChange={handleChange}
									onBlur={handleBlur}
									className={errors.password && touched.password && 'error'}
								/>
								{errors.password && touched.password && (
									<div className='input-response'>{errors.password}</div>
								)}
							</div>
							<ForgotPassword>
								<h4 className='secondary-font'>Forgot your password?</h4>
								<p>
									<a href='/jsp/registration/common/account_password.jsp'>
										Click here
									</a>{' '}
									to have a new one sent to you.
								</p>
							</ForgotPassword>
							<Modal.Footer>
								<div className={showProgressBar ? 'hide' : 'button-wrap'}>
									<button
										onClick={handleClose}
										type='button'
										className='btn btn-default guest'
									>
										Continue as a Guest
									</button>
									<button
										className='btn btn-primary'
										type='submit'
										disabled={isSubmitting}
									>
										Login and Checkout
									</button>
								</div>
								<div className={showProgressBar ? '' : 'hide'}>
									<ProgressBar animated />
								</div>
							</Modal.Footer>
						</LoginForm>
					</div>
				);
			}}
		</Formik>
	);
};
// END OF FUNCTIONAL COMPONENT

export default Validate;
